import { useEffect } from 'react';

import { CopyableToken } from '../../CopyableToken';
import { PLACEHOLDER_HELP_ITEMS } from '../placeholders';

/**
 * 放送地点モーダル右上の「？」ボタンから開くプレースホルダのヘルプ。
 *
 * 以前は各 SSML 入力欄の直下に欄ごとの凡例 (`PlaceholderLegend`) を置いていたが、
 * 画面が長くなるため、全コンテンツ種別・全判定モードのプレースホルダを
 * 1 つの一覧 (`PLACEHOLDER_HELP_ITEMS`) にまとめてここに表示する。
 * 欄ごとに使えるトークンが違うため「使える場所」列を併記する。
 */
export function PlaceholderHelpDialog({ onClose }: { onClose: () => void }): React.JSX.Element {
  // Esc で閉じる (背後の編集モーダルは閉じない)。
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [onClose]);

  return (
    <div
      className="modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label="プレースホルダのヘルプ"
      data-testid="placeholder-help-dialog"
      style={{ zIndex: 1200 }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal modal--single-column" style={{ maxWidth: 820 }}>
        <div className="modal-header">
          <h2 style={{ margin: 0, fontSize: 15 }}>？ 利用できるプレースホルダ</h2>
          <button
            type="button"
            data-testid="placeholder-help-close-button"
            onClick={onClose}
            aria-label="ヘルプを閉じる"
          >
            ×
          </button>
        </div>
        <div className="modal-body modal-body--single-column" style={{ padding: 16 }}>
          <p className="hint" style={{ marginTop: 0 }}>
            SSML テンプレートに記述すると、放送時に実際の値へ置き換えられます。
            トークンをクリックするとクリップボードにコピーできます。
            「使える場所」に無い欄で使うと空欄として扱われます。
          </p>
          <table className="placeholder-help-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>プレースホルダ</th>
                <th style={{ textAlign: 'left' }}>説明</th>
                <th style={{ textAlign: 'left' }}>使える場所</th>
              </tr>
            </thead>
            <tbody>
              {PLACEHOLDER_HELP_ITEMS.map((item) => (
                <tr key={item.token}>
                  <td>
                    <CopyableToken token={item.token} />
                  </td>
                  <td>{item.description}</td>
                  <td>{item.usage}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
